import { Eye, Edit, Trash2, Download, Share2, MoreHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ContentItem } from '@/hooks/useCompanyContent'
import { useState } from 'react'

interface ContentQuickActionsProps {
  content: ContentItem
  onView?: (content: ContentItem) => void
  onEdit?: (content: ContentItem) => void
  onDelete?: (content: ContentItem) => void
  onDownload?: (content: ContentItem) => void
  onShare?: (content: ContentItem) => void
  showViewOnly?: boolean
}

export function ContentQuickActions({
  content,
  onView,
  onEdit,
  onDelete,
  onDownload,
  onShare,
  showViewOnly = false
}: ContentQuickActionsProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const handleView = () => {
    if (onView) {
      onView(content)
    }
  }

  const handleEdit = () => {
    if (onEdit) {
      onEdit(content)
    }
  }

  const handleDelete = () => {
    if (showDeleteConfirm) {
      if (onDelete) {
        onDelete(content)
      }
      setShowDeleteConfirm(false)
    } else {
      setShowDeleteConfirm(true)
    }
  }

  const handleDownload = () => {
    if (onDownload) {
      onDownload(content)
    }
  }

  const handleShare = () => {
    if (onShare) {
      onShare(content)
    }
  }

  // If showViewOnly is true, only show the View button
  if (showViewOnly) {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={handleView}
        className="h-8 w-8 p-0"
      >
        <Eye className="w-4 h-4" />
      </Button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {/* View Action */}
        <DropdownMenuItem onClick={handleView}>
          <Eye className="w-4 h-4 mr-2" />
          View
        </DropdownMenuItem>

        {/* Edit Action (only if onEdit is provided) */}
        {onEdit && (
          <DropdownMenuItem onClick={handleEdit}>
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </DropdownMenuItem>
        )}

        {/* Download Action */}
        {onDownload && (
          <DropdownMenuItem onClick={handleDownload}>
            <Download className="w-4 h-4 mr-2" />
            Download
          </DropdownMenuItem>
        )}

        {/* Share Action */}
        {onShare && (
          <DropdownMenuItem onClick={handleShare}>
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </DropdownMenuItem>
        )}

        {/* Delete Action (only if onDelete is provided) */}
        {onDelete && (
          <>
            <DropdownMenuItem
              onClick={handleDelete}
              className={showDeleteConfirm ? 'bg-red-500/10 text-red-600' : ''}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {showDeleteConfirm ? 'Confirm Delete' : 'Delete'}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface ContentActionButtonsProps {
  content: ContentItem
  onView?: (content: ContentItem) => void
  onEdit?: (content: ContentItem) => void
  onDelete?: (content: ContentItem) => void
  onDownload?: (content: ContentItem) => void
  showViewOnly?: boolean
}

export function ContentActionButtons({
  content,
  onView,
  onEdit,
  onDelete,
  onDownload,
  showViewOnly = false
}: ContentActionButtonsProps) {
  const handleView = () => {
    if (onView) {
      onView(content)
    }
  }

  const handleEdit = () => {
    if (onEdit) {
      onEdit(content)
    }
  }

  const handleDelete = () => {
    if (onDelete) {
      onDelete(content)
    }
  }

  const handleDownload = () => {
    if (onDownload) {
      onDownload(content)
    }
  }

  // If showViewOnly is true, only show the View button
  if (showViewOnly) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={handleView}
        className="h-8"
      >
        <Eye className="w-4 h-4 mr-1" />
        View
      </Button>
    )
  }

  return (
    <div className="flex items-center gap-2">
      {/* View Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={handleView}
        className="h-8"
      >
        <Eye className="w-4 h-4 mr-1" />
        View
      </Button>

      {/* Download Button */}
      {onDownload && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownload}
          className="h-8"
        >
          <Download className="w-4 h-4" />
        </Button>
      )}

      {/* Edit Button (only if onEdit is provided) */}
      {onEdit && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleEdit}
          className="h-8"
        >
          <Edit className="w-4 h-4" />
        </Button>
      )}

      {/* Delete Button (only if onDelete is provided) */}
      {onDelete && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleDelete}
          className="h-8 text-red-600 hover:text-red-700 hover:bg-red-500/10"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      )}
    </div>
  )
}
